import type { Attachment } from "@/hooks/queries/use-fogbugz";
import { buildAttachmentProxyUrl } from "@/lib/fogbugz-attachment";
import { safeHttpUrl } from "@/lib/safe-url";
import { useAuthenticatedImage } from "@/hooks/use-authenticated-image";
import { FileTypeIcon } from "./file-type-icon";

interface AttachmentThumbnailProps {
  attachment: Attachment;
  onOpen: (src: string, alt: string) => void;
}

export function AttachmentThumbnail({
  attachment,
  onOpen,
}: AttachmentThumbnailProps) {
  // Try proxy URL first (for relative default.asp?pg=pgDownload URLs).
  // Fall back to direct URL if sURL is already a valid HTTP link (pre-signed CDN).
  const proxyUrl = buildAttachmentProxyUrl(attachment.sURL, { inline: true });
  const directUrl = !proxyUrl ? safeHttpUrl(attachment.sURL) : null;
  const imageUrl = proxyUrl ?? directUrl;
  // Only use authenticated fetch for proxy URLs; direct CDN URLs are pre-signed
  const { src, loading, error } = useAuthenticatedImage(proxyUrl);
  const effectiveSrc = proxyUrl ? src : directUrl;
  const effectiveLoading = proxyUrl ? loading : false;
  const effectiveError = proxyUrl ? error : false;

  if (!imageUrl || effectiveError) {
    return (
      <div
        className="flex h-24 w-24 items-center justify-center rounded border border-border bg-muted"
        title={`${attachment.sFileName} (URL unavailable)`}
      >
        <FileTypeIcon filename={attachment.sFileName} className="h-8 w-8 text-muted-foreground" />
      </div>
    );
  }

  if (effectiveLoading || !effectiveSrc) {
    return (
      <div
        className="flex h-24 w-24 items-center justify-center rounded border border-border bg-muted animate-pulse"
        title={`Loading ${attachment.sFileName}...`}
      />
    );
  }

  return (
    <button
      type="button"
      className="group relative h-24 w-24 overflow-hidden rounded border border-border
        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      onClick={() => onOpen(effectiveSrc, attachment.sFileName)}
      aria-label={`View ${attachment.sFileName}`}
    >
      <img
        src={effectiveSrc}
        alt={attachment.sFileName}
        className="h-full w-full object-cover transition-transform group-hover:scale-105"
      />
    </button>
  );
}
